import React, { useEffect, useMemo, useState } from 'react';
import { FormInstance, UploadFile } from 'antd';
import {
  DisputeCommentInput,
  DisputeDiffSection,
  DisputeFilesInput,
  DisputeValuesForm,
  DuplicatedDisputeAlert,
  InvoiceItemDiffLine,
  ValuesOnFinishType,
} from '@pay-bill/components';
import {
  DISPUTE_PREVIEW_INFO_QUERY,
  InvoiceItemQuery,
} from '@pay-bill/graphql';
import {
  Link,
  DocumentPreviewModalDeprecatedProvider,
  ErrorHandler,
  LoadingState,
  Form,
  Col,
  Row,
  Text,
  Divider,
} from '@common/components';
import { staffingRequestTypeHumanized } from 'works/legacy/services/staffing-request-type-service';
import {
  toISODateFormat,
  toUSCompactDateFormat,
} from '@common/services/datetime';
import { useQuery } from '@common/hooks';
import { useCurrentEntity } from '@identity/hooks';
import { useHasValidErrorDisputeFields } from '@pay-bill/hooks';
import { AutoAnimatedHeight } from 'works/legacy/components/auto-animated-height';
import { Store } from 'antd/lib/form/interface';
import { debounce } from 'lodash';

type FormValues = {
  comment?: string;
  files?: UploadFile[];
  newHours?: number;
  billingAmount?: number;
  newPayCode?: string;
  finalRateCents?: number;
};

export const DisputeCreateDrawerBody = ({
  invoiceItemId,
  onFinish,
  setLoading,
  form,
}: {
  invoiceItemId: string;
  onFinish: (values: ValuesOnFinishType) => void;
  setLoading: (value: boolean) => void;
  form: FormInstance;
}) => {
  const currentEntity = useCurrentEntity();
  const entityType = currentEntity.entityType;

  const [formValues, setFormValues] = useState<FormValues>({
    newHours: undefined,
    newPayCode: undefined,
    comment: undefined,
    finalRateCents: undefined,
  });

  const {
    data: invoiceItemData,
    loading: invoiceItemLoading,
    error: invoiceItemError,
  } = useQuery(InvoiceItemQuery, {
    variables: { id: invoiceItemId },
    skip: !invoiceItemId,
  });

  const invoiceItem = invoiceItemData?.worksInvoiceItem;
  const staffingRequestAssignmentId =
    invoiceItem?.staffingRequestAssignment?.id;

  const previewVariables = useMemo(() => {
    if (
      !staffingRequestAssignmentId ||
      !formValues.newPayCode ||
      !invoiceItem.date
    ) {
      return undefined;
    }

    return {
      newHours: formValues.newHours || 0,
      newDate: toISODateFormat(invoiceItem.date),
      newPayCode: formValues.newPayCode,
      finalRateCents: formValues.finalRateCents
        ? Math.floor(formValues.finalRateCents * 100)
        : undefined,
      staffingRequestAssignmentId,
    };
  }, [
    formValues.finalRateCents,
    formValues.newHours,
    formValues.newPayCode,
    invoiceItem?.date,
    staffingRequestAssignmentId,
  ]);

  const {
    data: previewData,
    loading: loadingPreview,
    error: previewError,
  } = useQuery(DISPUTE_PREVIEW_INFO_QUERY, {
    skip: !previewVariables,
    variables: previewVariables,
  });

  function onResetBillingRate() {
    if (!previewVariables) return;

    setFormValues({
      ...previewVariables,
      finalRateCents: undefined,
    });
  }

  const formInvalid = !useHasValidErrorDisputeFields(form, formValues);
  const { previewRate, disputeMatches } = previewData || {};
  const hasDuplication = !!(
    disputeMatches?.dispute || disputeMatches?.invoiceItem
  );

  useEffect(() => {
    setLoading(
      loadingPreview || hasDuplication || formInvalid || invoiceItemLoading
    );
  }, [
    loadingPreview,
    hasDuplication,
    formInvalid,
    invoiceItemLoading,
    setLoading,
  ]);

  const error = previewError || invoiceItemError;
  if (error) {
    return <ErrorHandler error={error} log="DisputeCreateDrawerBody" />;
  }

  if (invoiceItemLoading && !invoiceItemData) {
    return <LoadingState />;
  }

  const reverseInvoiceId = invoiceItem.worksReverseInvoice.id;
  const invoiceId = invoiceItem.worksInvoice.id;
  const organizationPayCodes = invoiceItem.organization.payCodes;

  const fullName = invoiceItem.worksProfile.fullName;
  const date = toUSCompactDateFormat(invoiceItem.date);
  const reverseInvoiceCode = invoiceItem.worksReverseInvoice.code;
  const organizationName = invoiceItem.organization.name;
  const contractType = staffingRequestTypeHumanized(
    invoiceItem.staffingRequestType
  );

  const onFormFinish = (values: Store) => {
    onFinish({
      staffingRequestAssignmentId: staffingRequestAssignmentId,
      invOrRinvId: entityType === 'Organization' ? invoiceId : reverseInvoiceId,
      entityType: entityType,
      worksInvoiceItemId: invoiceItem.id,
      files: values.files || [],
      input: {
        newHours: values.newHours,
        newPayCode: values.newPayCode,
        newDate: toISODateFormat(invoiceItem.date),
        disputeType: 'error',
        creationReason: values.comment,
        finalRateCents: values.finalRateCents * 100,
      },
    });
  };

  const onFormChange = debounce(
    (changes: FormValues, formValues: FormValues) => {
      const updatedValues = {
        ...formValues,
        ...changes,
      };

      if (changes.newPayCode) {
        updatedValues.finalRateCents = undefined;
      }

      if (Array.isArray(updatedValues.newHours)) {
        updatedValues.newHours = updatedValues.newHours[1];
      }

      setFormValues(updatedValues);
    },
    500
  );

  return (
    <>
      <div className="dispute-create-drawer-body">
        <Row className="dispute-details">
          <Col span={8}>
            <Text secondary>Name</Text>
          </Col>
          <Col span={16}>
            <Link
              className="color-elm"
              to={`/works/supplier/candidates/${invoiceItem.worksProfile.id}`}
              target="_blank"
            >
              <strong>{fullName}</strong>
            </Link>
          </Col>
        </Row>

        <Row className="dispute-details">
          <Col span={8}>
            <Text secondary>Reverse Invoice #</Text>
          </Col>
          <Col span={16}>
            <Link
              className="color-elm"
              to={`/works/supplier/billing/reverse-invoices/${reverseInvoiceId}`}
              target="_blank"
            >
              <strong>{reverseInvoiceCode}</strong>
            </Link>
          </Col>
        </Row>

        <Row className="dispute-details">
          <Col span={8}>
            <Text secondary>Organization</Text>
          </Col>
          <Col span={16}>{organizationName}</Col>
        </Row>

        <Row className="dispute-details">
          <Col span={8}>
            <Text secondary>Date Worked</Text>
          </Col>
          <Col span={16}>{date}</Col>
        </Row>

        <Row className="dispute-details">
          <Col span={8}>
            <Text secondary>Contract Type</Text>
          </Col>
          <Col span={16}>{contractType}</Col>
        </Row>

        <Divider gutter={16} />

        <Form
          onFinish={onFormFinish}
          layout="vertical"
          form={form}
          initialValues={formValues}
          onValuesChange={(changes, formValues) =>
            onFormChange(changes, formValues)
          }
        >
          <DisputeDiffSection mode="form">
            <InvoiceItemDiffLine item={invoiceItem} />
            <DisputeValuesForm
              form={form}
              loadingPreview={loadingPreview}
              payCodes={organizationPayCodes}
              billingRate={previewRate?.finalRateCents || 0}
              billingAmountCents={previewRate?.billingAmountCents || 0}
              showEstimationSymbol={invoiceItem.worksInvoice.preCalculated}
              onResetBillingRate={onResetBillingRate}
            />
            {invoiceItem.worksInvoice.preCalculated && (
              <p className="estimation-message">
                * Billing Amount is an estimate and may differ from the final
                amount.
              </p>
            )}
          </DisputeDiffSection>
          <AutoAnimatedHeight visible={hasDuplication}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <DuplicatedDisputeAlert
                  dispute={disputeMatches?.dispute}
                  invoiceItem={disputeMatches?.invoiceItem}
                />
              </Col>
            </Row>
          </AutoAnimatedHeight>
          <DisputeCommentInput creationType />
          <Row className="dispute-upload-row">
            <Col span={24}>
              <DocumentPreviewModalDeprecatedProvider>
                <DisputeFilesInput maxFilesSlots={3} />
              </DocumentPreviewModalDeprecatedProvider>
            </Col>
          </Row>
        </Form>
      </div>
      <style jsx>
        {`
          p {
            margin-bottom: 2px;
          }
          .estimation-message {
            padding-bottom: 12px;
          }
          .dispute-create-drawer-body :global(.select-option div) {
            white-space: break-spaces;
            word-break: break-word;
          }
          .dispute-create-drawer-body :global(.dispute-upload-row) {
            margin-top: 8px;
            max-width: 100%;
          }
          :global(.dispute-details) {
            margin-bottom: 8px;
          }
        `}
      </style>
    </>
  );
};
